import { keys, omit } from 'ramda';
import type { NodeEntry, Path } from 'slate';
import { Editor, Element, Node, Text, Transforms } from 'slate';

import { mergeWithDefaults } from '../../../util';
import type { AlineText, AlineTextFormats, ParagraphElement, ParagraphProps } from '../../internal';
import {
  createDefaultParagraph,
  getIsTextNodeWithComments,
  getIsTextNodeWithoutComments,
  isFooterElement,
  isHeaderElement,
  isLinkElement,
  isPageBreakElement,
  isParagraphEntry,
  isTableCellElement,
  linksShouldBeMerged,
  Marks,
  normalizeInline,
  propsEq,
  Queries,
} from '../../internal';

export function normalizeParagraph(
  editor: Editor,
  paragraphEntry: NodeEntry<ParagraphElement>
): void {
  const [paragraph, paragraphPath] = paragraphEntry;
  // If the paragraph is empty, remove it from the tree and exit.
  if (!paragraph.children.length) {
    Transforms.removeNodes(editor, {
      at: paragraphPath,
      voids: true,
    });
    return;
  }

  const styleSheet = Queries.activeStyleSheet(editor);
  const styleVariant = Queries.renderedParagraphStyleVariant(paragraph);
  const paragraphFontStyles =
    styleVariant in styleSheet ? styleSheet[styleVariant].styles : Marks.default;
  const paraWithinNonPageBreakNode = Boolean(
    Editor.above(editor, {
      at: paragraphPath,
      match: n => isTableCellElement(n) || isHeaderElement(n) || isFooterElement(n),
    })
  );

  /**
   * Normalize children.
   * n is used to keep track of an index of inserted/removed/merged nodes since we apply operations while iterating.
   */
  for (let i = 0, n = 0; i < paragraph.children.length; i++, n++) {
    let childNode: Node = paragraph.children[i];

    // Important. Get the prevNode each time as it may have changed during merging/removing/inserting nodes.
    const prevNode: Node | undefined =
      n > 0 ? Node.get(editor, paragraphPath.concat(n - 1)) : undefined;
    const isLastChild = i === paragraph.children.length - 1;

    // Paragraphs should not contain other block-level nodes.  Unwrap all nested blocks and only allow text and inlines as children.
    while (Element.isElement(childNode) && Editor.isBlock(editor, childNode)) {
      Transforms.unwrapNodes(editor, { at: paragraphPath.concat(n), voids: true });
      childNode = Node.get(editor, paragraphPath.concat(n));
    }

    if (paraWithinNonPageBreakNode && isPageBreakElement(childNode)) {
      Transforms.removeNodes(editor, { at: paragraphPath.concat(n) });
      n--;
      continue;
    }

    if (Element.isElement(childNode) && Editor.isInline(editor, childNode)) {
      // Merge the childNode inline into the prevNode if both are links with the same URL.
      if (
        prevNode &&
        Element.isElement(prevNode) &&
        Editor.isInline(editor, prevNode) &&
        isLinkElement(childNode) &&
        isLinkElement(prevNode) &&
        linksShouldBeMerged(childNode, prevNode)
      ) {
        const { children, ...properties } = prevNode;

        // Use editor.apply instead of AlineTransforms.mergeNodes as applying the op directly has performance benefits (particularly for changes to large docs).
        editor.apply({
          path: paragraphPath.concat(n),
          position: prevNode.children.length,
          properties,
          type: 'merge_node',
        });

        n--;

        /**
         * Normalize the inline to merge adjacent text nodes inside the inline that match.
         * Make sure this is AFTER everything above so we normalize the final, merged inline if a merge took place.
         */
        normalizeInline(editor, paragraphPath.concat(n));
      } else if (!prevNode || (Element.isElement(prevNode) && Editor.isInline(editor, prevNode))) {
        const textNode = constructTextNode(editor, paragraphPath, paragraphFontStyles, n);
        Transforms.insertNodes(editor, textNode, { at: paragraphPath.concat(n) });
        n++;
      } else if (getIsTextNodeWithComments(prevNode)) {
        // Ensure a text node is before childNode (inline) if prevNode has different comment than the inline so a user can type "between comments".
        const inlineTextEntries = Node.texts(childNode);
        const firstInlineTextEntry = inlineTextEntries.next().value;
        const firstInlineText = firstInlineTextEntry?.[0];
        const inlineHasSameComment =
          !!firstInlineText && Queries.textNodesHaveIntersectingComments(prevNode, firstInlineText);
        if (!inlineHasSameComment) {
          const textNode = constructTextNode(editor, paragraphPath, paragraphFontStyles, n);
          Transforms.insertNodes(editor, textNode, { at: paragraphPath.concat(n) });
          n++;
        }
      }

      // If an inline is the last node in a paragraph, make sure there is a text node after it so a user can type.
      if (isLastChild) {
        const textNode = constructTextNode(editor, paragraphPath, paragraphFontStyles, n);
        Transforms.insertNodes(editor, textNode, { at: paragraphPath.concat(n + 1) });
        n++;
      }

      continue;
    }

    // Merge adjacent text nodes that match.
    if (
      prevNode &&
      Text.isText(childNode) &&
      Text.isText(prevNode) &&
      propsEq(childNode, prevNode)
    ) {
      const { text, ...properties } = prevNode;

      // Use editor.apply instead of AlineTransforms.mergeNodes as applying the op directly has performance benefits (particularly for changes to large docs).
      editor.apply({
        path: paragraphPath.concat(n),
        position: text.length,
        properties,
        type: 'merge_node',
      });

      n--;
      continue;
    }

    // Ensure a user can type before/after a text node w/ comments.
    if (getIsTextNodeWithComments(childNode) && childNode.text !== '') {
      const prevTextHasDiffComment =
        prevNode &&
        getIsTextNodeWithComments(prevNode) &&
        !Queries.textNodesHaveIntersectingComments(childNode, prevNode);

      if (
        !prevNode ||
        (Element.isElement(prevNode) && Editor.isInline(editor, prevNode)) ||
        prevTextHasDiffComment
      ) {
        const textNode = constructTextNode(editor, paragraphPath, paragraphFontStyles, n);
        Transforms.insertNodes(editor, textNode, { at: paragraphPath.concat(n) });
        n++;
      }

      if (isLastChild) {
        const textNode = constructTextNode(editor, paragraphPath, paragraphFontStyles, n);
        Transforms.insertNodes(editor, textNode, { at: paragraphPath.concat(n + 1) });
        n++;
      }

      continue;
    }

    // Remove "unnecessary" empty text nodes.
    if (Text.isText(childNode) && childNode.text === '') {
      // Use n since nodes may be removed and n is accurate while i is not.
      const isFirstChild = n === 0;
      const nextNode = Node.getIf(editor, [...paragraphPath, n + 1]);
      const nextIsTextWithoutComments = !!nextNode && getIsTextNodeWithoutComments(nextNode);
      const prevIsTextWithoutComments = !!prevNode && getIsTextNodeWithoutComments(prevNode);
      const childHasInsertOrDeleteEdit = textHasInsertOrDeleteEdit(childNode);
      const isFirstWithInsertOrDeleteEdit = isFirstChild && childHasInsertOrDeleteEdit;
      const isLastWithInsertOrDeleteEdit = isLastChild && childHasInsertOrDeleteEdit;
      const adjacentTextNodeWithoutComments =
        prevIsTextWithoutComments || nextIsTextWithoutComments;

      if (
        adjacentTextNodeWithoutComments &&
        !isFirstWithInsertOrDeleteEdit &&
        !isLastWithInsertOrDeleteEdit
      ) {
        Transforms.removeNodes(editor, { at: paragraphPath.concat(n) });
        n--;
      }
    }
  }
}

type ParagraphPropsKeys = keyof Omit<ParagraphElement, 'children'>;
const paragraphPropsKeys: ParagraphPropsKeys[] = keys(omit(['children'], createDefaultParagraph()));

export function validateParagraphProps(
  editor: Editor,
  paragraphEntry: NodeEntry<ParagraphElement>
): NodeEntry<ParagraphElement> {
  const [paragraph, paragraphPath] = paragraphEntry;
  const missingKeys = paragraphPropsKeys.filter(paragraphPropKey => {
    const paragraphHasKey = paragraphPropKey in paragraph;
    return !paragraphHasKey;
  });
  if (!missingKeys.length) return paragraphEntry;

  const { children: paraChildren, ...paragraphProps } = paragraph;
  const { children: defaultParaChildren, ...defaultParaProps } = createDefaultParagraph();
  const newProps: ParagraphProps = mergeWithDefaults(paragraphProps, defaultParaProps);

  Transforms.setNodes(editor, newProps, { at: paragraphPath, voids: true });

  const updatedEntry = Editor.node(editor, paragraphPath);
  if (!isParagraphEntry(updatedEntry)) return paragraphEntry;

  return updatedEntry;
}

/**
 * Utility function for constructing a text node that is inserted in a paragraph (e.g., around an inline).
 */
export function constructTextNode(
  editor: Editor,
  paragraphPath: Path,
  paragraphFontStyles: AlineTextFormats,
  n: number
): AlineText {
  const textNode: AlineText = {
    text: '',
    ...paragraphFontStyles,
    comments: [],
    suggestedEdits: [],
  };

  /**
   * If there is a text before the node at n (i.e., the inline), use its
   * formats. Found structurally rather than with `Editor.before`: the point
   * walkers require an end text node in every block they pass, so they throw
   * on the very shape this normalization repairs (a paragraph ending in a
   * childless inline). No preceding text means the inline sits at the start
   * of the paragraph — keep the paragraph styles.
   */
  const paragraphNode = Node.get(editor, paragraphPath);
  if (!Element.isElement(paragraphNode)) return textNode;
  const textBeforeInline = findLastTextAmongFirstChildren(paragraphNode, n);
  if (textBeforeInline) {
    // Only carry over fontSize, fontType on purpose.
    const { fontSize, fontType } = textBeforeInline;
    /**
     * Contrary to what TypeScript believes, fontSize and fontType can be undefined if they have
     * not yet been added to a text node that was missing them. In this case, avoid adding them to
     * the text node since explicit undefined property values can make the spread operator non-
     * type safe. https://github.com/microsoft/TypeScript/issues/57086
     */
    if (fontSize) textNode.fontSize = fontSize;
    if (fontType) textNode.fontType = fontType;
  }

  return textNode;
}

/** The last text leaf among the element's first `count` children, if any. */
function findLastTextAmongFirstChildren(element: Element, count: number): Text | undefined {
  for (let i = Math.min(count, element.children.length) - 1; i >= 0; i--) {
    const child = element.children[i];
    if (child === undefined) continue;
    const found = findLastTextIn(child);
    if (found) return found;
  }
  return undefined;
}

/** The last text leaf inside the node in document order, if any. */
function findLastTextIn(node: Node): Text | undefined {
  if (Text.isText(node)) return node;
  if (Editor.isEditor(node)) return undefined;
  for (let i = node.children.length - 1; i >= 0; i--) {
    const child = node.children[i];
    if (child === undefined) continue;
    const found = findLastTextIn(child);
    if (found) return found;
  }
  return undefined;
}

function textHasInsertOrDeleteEdit(textNode: AlineText): boolean {
  return !!textNode.suggestedEdits?.some(
    edit => edit.type === 'insertNode' || edit.type === 'deleteNode'
  );
}
